// Canonical SPA tenancy URLs (Phase 1 / T1.6A, Final Design v1.1 §11.1).
//
// Two shapes, and only two:
//
//   /app/b/:businessId/<tail>                    a BUSINESS-level screen
//   /app/b/:businessId/l/:locationId/<tail>      a LOCATION-level screen
//
// They mirror the server exactly (apps/dashboard/src/lib/tenant-paths.ts), so a
// screen's URL and the API it calls name the same tenant.
//
// WHAT CHANGED, AND WHY IT MATTERS. `/app/b/{id}/…` used to mean a SITE id, and
// business-level screens (billing, settings, …) carried no tenant at all. Both
// are gone: a Location helper now REQUIRES both ids and cannot be called with
// one, which is what stops a Location URL from being built out of whatever id
// happened to be at hand. `legacyRedirect` below never guesses that an old id
// is a Business id — an old Location bookmark is resolved through a
// membership-checked server lookup before anyone is sent anywhere.
export type Layer = 'foundation' | 'visibility' | 'reputation' | 'pulse';

/** Branded so a Location helper cannot be handed a Business id positionally. */
export type BusinessId = string;
export type LocationId = string;

const b = (id: BusinessId) => `/app/b/${id}`;
const l = (bid: BusinessId, lid: LocationId) => `/app/b/${bid}/l/${lid}`;

/**
 * BUSINESS-level screens: they name no Location because they have none. Every
 * one of them reads a business-scoped API (billing, domains, mailboxes,
 * surveys, member prefs, the location list itself).
 */
export const businessRoutes = {
  billing: (bid: BusinessId, opts?: { plan?: string; from?: string }) => {
    const params = new URLSearchParams();
    if (opts?.plan) params.set('plan', opts.plan);
    if (opts?.from) params.set('from', opts.from);
    const qs = params.toString();
    return qs ? `${b(bid)}/billing?${qs}` : `${b(bid)}/billing`;
  },
  team: (bid: BusinessId) => `${b(bid)}/team`,
  settings: (bid: BusinessId) => `${b(bid)}/settings`,
  locations: (bid: BusinessId) => `${b(bid)}/locations`,
  mailboxes: (bid: BusinessId) => `${b(bid)}/mailboxes`,
  surveys: (bid: BusinessId) => `${b(bid)}/surveys`,
  survey: (bid: BusinessId, id: string) => `${b(bid)}/surveys/${id}`,
  surveyEdit: (bid: BusinessId, id: string) => `${b(bid)}/surveys/${id}/edit`,
} as const;

/**
 * LOCATION-level screens. Every helper takes BOTH ids — a Location is only ever
 * meaningful inside its Business, and the resolver proves that pairing on every
 * request. There is no single-argument form, by construction.
 */
export const locationRoutes = {
  overview: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/overview`,
  setup: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/setup`,
  layer: (bid: BusinessId, lid: LocationId, layer: Layer) => `${l(bid, lid)}/${layer}`,
  businessInfo: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/foundation/business-info`,
  website: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/foundation/website`,
  websiteDomain: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/foundation/website/domain`,
  domainWizard: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/foundation/website/domain/setup`,
  pageEdit: (bid: BusinessId, lid: LocationId, pageId: string) =>
    `${l(bid, lid)}/foundation/website/pages/${pageId}/edit`,
  getFoundReady: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/foundation/get-found-ready`,
  googleProfile: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/visibility/google-profile`,
  googleProfileContent: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/visibility/google-profile/content`,
  googleProfileMap: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/visibility/google-profile/map`,
  aiVisibility: (bid: BusinessId, lid: LocationId, engine?: string) =>
    engine ? `${l(bid, lid)}/visibility/ai/${engine}` : `${l(bid, lid)}/visibility/ai`,
  aeo: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/visibility/aeo`,
  search: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/visibility/search`,
  reviews: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/reputation/reviews`,
  reputationSettings: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/reputation/settings`,
  inbox: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/pulse/inbox`,
  leads: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/pulse/leads`,
  analytics: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/pulse/analytics`,
  reports: (bid: BusinessId, lid: LocationId) => `${l(bid, lid)}/reports`,
} as const;

/** IDENTITY / global screens: they name no tenant, and must not. */
export const appRoutes = {
  index: () => '/app',
  /** Canonical onboarding. T1.6B builds the screen; this is its address. */
  start: (opts?: { resume?: string; place?: string; claim?: string; draft?: string; mode?: 'add-business' }) => {
    const params = new URLSearchParams();
    // The EXPLICIT second-Business intent (T1.7 §6). The switcher's "Add a
    // business" carries it so the server receives a creation intent it can
    // validate — reaching /app/start must never mint a tenant on its own.
    if (opts?.mode) params.set('mode', opts.mode);
    if (opts?.resume) params.set('resume', opts.resume);
    if (opts?.place) params.set('place', opts.place);
    if (opts?.claim) params.set('claim', opts.claim);
    if (opts?.draft) params.set('draft', opts.draft);
    const qs = params.toString();
    return qs ? `/app/start?${qs}` : '/app/start';
  },
} as const;

export const routes = { ...appRoutes, ...businessRoutes, ...locationRoutes } as const;

// =====================================================================
// Legacy URLs
// =====================================================================

/** An old URL that named a Location and must be translated before we navigate. */
export interface LegacyLocationTarget {
  /** The Location ULID the old URL carried. NOT a Business id. */
  legacyLocationId: string;
  /** Rebuilds the canonical URL once the server has told us the real ids. */
  rebuild: (bid: BusinessId, lid: LocationId) => string;
  search: string;
}

/** Everything a legacy path can turn into. */
export type LegacyResolution =
  | { kind: 'redirect'; to: string }
  | { kind: 'resolve-location'; target: LegacyLocationTarget }
  | null;

// The tails a BUSINESS-level screen owns. `/app/b/{id}/billing` is a real,
// current URL — it must never be mistaken for an old `/app/b/{siteId}/…`
// bookmark and sent through the Location lookup. This list is what makes the
// two shapes decidable, and a test pins it against businessRoutes.
export const BUSINESS_TAILS = ['billing', 'team', 'settings', 'locations', 'mailboxes', 'surveys'] as const;

// Old `/app/b/{siteId}/<tail>` → the canonical Location tail, semantics
// preserved: overview stays overview, a visibility child stays that child.
const LOCATION_TAIL_MAP: [RegExp, (m: RegExpMatchArray) => (bid: string, lid: string) => string][] = [
  [/^foundation\/website\/pages\/([^/]+)\/edit$/, (m) => (bid, lid) => locationRoutes.pageEdit(bid, lid, m[1]!)],
  [/^visibility\/ai\/([^/]+)$/, (m) => (bid, lid) => locationRoutes.aiVisibility(bid, lid, m[1]!)],
  [/^(.*)$/, (m) => (bid, lid) => `${l(bid, lid)}/${m[1]}`],
];

function locationRebuilder(tail: string): (bid: string, lid: string) => string {
  for (const [re, make] of LOCATION_TAIL_MAP) {
    const m = tail.match(re);
    if (m) return make(m);
  }
  /* c8 ignore next */
  return (bid, lid) => locationRoutes.overview(bid, lid);
}

// Old `/app/sites/{siteId}/<x>` → the canonical Location tail it became.
const SITES_TAILS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^pages\/([^/]+)\/edit$/, (m) => `foundation/website/pages/${m[1]}/edit`],
  [/^business-info$/, () => 'foundation/business-info'],
  [/^domain$/, () => 'foundation/website/domain'],
  [/^gbp$/, () => 'visibility/google-profile'],
  [/^rank$/, () => 'visibility/google-profile/map'],
  [/^ai-visibility$/, () => 'visibility/ai'],
  [/^get-found$/, () => 'visibility/aeo'],
  [/^reviews$/, () => 'reputation/reviews'],
  [/^forms$/, () => 'pulse/inbox'],
  [/^analytics$/, () => 'pulse/analytics'],
];

/**
 * Classify a legacy path.
 *
 * `redirect` is a straight client-side move — used only where the destination
 * names no tenant. `resolve-location` means the URL carries an OLD LOCATION ID
 * that must go through the membership-checked server lookup first: we never
 * assume it is a Business id, so a stale or foreign bookmark cannot become a
 * cross-tenant navigation. `null` means "not a legacy path".
 */
export function legacyRedirect(pathname: string, search: string): LegacyResolution {
  // Tenant-free legacy paths first: nothing to look up.
  if (/^\/app\/sites\/new$/.test(pathname)) return { kind: 'redirect', to: appRoutes.start() + search };
  if (/^\/app\/new$/.test(pathname)) return { kind: 'redirect', to: appRoutes.start() + search };
  if (/^\/app\/sites\/?$/.test(pathname)) return { kind: 'redirect', to: '/app' + search };

  const sites = pathname.match(/^\/app\/sites\/([^/]+)(?:\/(.*))?$/);
  if (sites) {
    const legacyLocationId = sites[1]!;
    const rest = sites[2] ?? '';
    let tail = 'overview';
    for (const [re, make] of SITES_TAILS) {
      const m = rest.match(re);
      if (m) {
        tail = make(m);
        break;
      }
    }
    const rebuilder = locationRebuilder(tail);
    return { kind: 'resolve-location', target: { legacyLocationId, rebuild: rebuilder, search } };
  }

  const bPath = pathname.match(/^\/app\/b\/([^/]+)(?:\/(.*))?$/);
  if (bPath) {
    const id = bPath[1]!;
    const rest = bPath[2] ?? '';
    // Already canonical: `/app/b/{businessId}/l/{locationId}/…`.
    if (rest.startsWith('l/')) return null;
    // A BUSINESS-level screen. `/app/b/{id}/billing` is current, not legacy.
    const head = rest.split('/')[0] ?? '';
    if ((BUSINESS_TAILS as readonly string[]).includes(head)) return null;
    // Anything else under /app/b/{id}/ is an OLD Location bookmark: the id is a
    // Location ULID from before the rename. It is NOT treated as a Business id.
    return {
      kind: 'resolve-location',
      target: { legacyLocationId: id, rebuild: locationRebuilder(rest || 'overview'), search },
    };
  }

  return null;
}
